package org.lennycodes.hldi.styles

import com.varabyte.kobweb.compose.css.BorderCollapse
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderCollapse
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.lennycodes.hldi.constants.HLDITheme


// ----- Styles -----
val UsersHeaderRowStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .margin(0.px, 0.px, 20.px)
            .display(DisplayStyle.Flex)
            .styleModifier {
                property("align-items", "center")
                property("justify-content", "space-between")
            }
    }
}
val UsersTitleStyle = CssStyle {
    base {
        Modifier
            .fontSize(22.px)
            .fontWeight(FontWeight.Bold)
            .color(HLDITheme.Colors.text)
            .margin(0.px)
    }
}

val AddAdminButtonStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(HLDITheme.Colors.primary)
            .color(Colors.White)
            .fontSize(14.px)
            .fontWeight(FontWeight.SemiBold)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("border", "none")
                property("border-radius", "10px")
                property("transition", "background-color 200ms ease, transform 150ms ease")
            }
    }
    hover {
        Modifier.styleModifier { property("transform", "translateY(-1px)") }
    }
}

// Scroll wrapper — the key mobile-friendliness fix: on narrow screens the
// table scrolls horizontally inside its rounded card instead of squashing
// or overflowing the page.
val TableCardStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .backgroundColor(Colors.White)
            .borderRadius(16.px)
            .styleModifier {
                property("box-shadow", "0 2px 12px ${Color.rgba(0, 0, 0, 0.08f)}")
                property("overflow-x", "auto")
                property("-webkit-overflow-scrolling", "touch")
            }
    }
}

val AdminTableStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .borderCollapse(BorderCollapse.Collapse)
            .styleModifier {
                property("min-width", "560px") // forces scroll instead of crush on mobile
            }
    }
}

val TableHeaderCellStyle = CssStyle {
    base {
        Modifier
            .fontSize(12.px)
            .fontWeight(FontWeight.SemiBold)
            .color(HLDITheme.Colors.mediumGray)
            .textAlign(TextAlign.Left)
            .styleModifier {
                property("text-transform", "uppercase")
                property("letter-spacing", "0.05em")
                property("border-bottom", "1.5px solid #EEEEEE")
                property("white-space", "nowrap")
            }
    }
}

val TableRowStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("border-bottom", "1px solid #F2F2F2")
            property("transition", "background-color 150ms ease")
        }
    }
    hover {
        Modifier.styleModifier { property("background-color", "#FAFAFA") }
    }
}

val TableCellStyle = CssStyle {
    base {
        Modifier

            .fontSize(14.px)
            .color(HLDITheme.Colors.text)
            .styleModifier { property("white-space", "nowrap") }
    }
}

val AvatarBadgeStyle = CssStyle {
    base {
        Modifier
            .width(36.px)
            .height(36.px)
            .borderRadius(50.percent)
            .backgroundColor(HLDITheme.Colors.accent)
            .color(HLDITheme.Colors.primary)
            .fontSize(13.px)
            .fontWeight(FontWeight.Bold)
            .display(DisplayStyle.Flex)
            .styleModifier {
                property("align-items", "center")
                property("justify-content", "center")
                property("flex-shrink", "0")
            }
    }
}

val NameCellStyle = CssStyle {
    base {
        Modifier
            .display(DisplayStyle.Flex)
            .styleModifier {
                property("align-items", "center")
                property("gap", "10px")
            }
    }
}

val IconButtonStyle = CssStyle {
    base {
        Modifier
            .width(32.px)
            .height(32.px)
            .display(DisplayStyle.Flex)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("align-items", "center")
                property("justify-content", "center")
                property("border", "none")
                property("border-radius", "8px")
                property("transition", "background-color 150ms ease")
            }
    }
}

val EditIconButtonStyle = CssStyle {
    base {
        Modifier.backgroundColor(Color.rgba(25, 118, 210, 0.1f))
            .color(HLDITheme.Colors.primary)
    }
    hover {
        Modifier.backgroundColor(Color.rgba(25, 118, 210, 0.2f))
    }
}

val DeleteIconButtonStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Color.rgba(229, 57, 53, 0.1f))
            .color(Color.rgb(229, 57, 53))
    }
    hover {
        Modifier.backgroundColor(Color.rgba(229, 57, 53, 0.2f))
    }
}
